import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import Icon from '../Icon';
import Tooltip from '../Tooltip';
import { ACTION_CHIP_REMOVE_ICON, ACTION_CHIP_TONE, ACTION_CHIP_TONES } from './constants';

// Design System — ActionChip
// A summary of one configured thing — an automation, an integration, a rule — as a chip:
// leading icon, a bold `label`, the `description` after it, and an optional remove button
// split off by a divider. The body is a button only when `onClick` is given, so a chip that
// cannot be opened reads as static; the same for the remove half and `onRemove`. Long text
// truncates with an ellipsis inside `maxWidth`, and `tooltip` puts the whole string within
// reach on hover. `slotClassNames` puts a caller's own class on the inner halves.
// Styles: ./index.less ships via less/ats_style.less (white-label pipeline).

const ActionChip = ({
    icon,
    label,
    description,
    onClick,
    onRemove,
    removeLabel,
    tooltip,
    tone = ACTION_CHIP_TONE.DEFAULT,
    disabled = false,
    className,
    slotClassNames = {},
    ...rest
}) => {
    const clickable = typeof onClick === 'function' && !disabled;
    const removable = typeof onRemove === 'function' && !disabled;

    const chip = (
        <span
            className={classNames(
                'ds-action-chip',
                `ds-action-chip--${ACTION_CHIP_TONES.includes(tone) ? tone : ACTION_CHIP_TONE.DEFAULT}`,
                {
                    'ds-action-chip--static': !clickable,
                    'ds-action-chip--disabled': disabled,
                },
                className,
            )}
            {...rest}
        >
            <button
                type="button"
                className={classNames('ds-action-chip__main', slotClassNames.main)}
                disabled={!clickable}
                onClick={clickable ? onClick : undefined}
            >
                {icon && <Icon value={icon} noMargin className="ds-action-chip__icon" />}
                <span className="ds-action-chip__text">
                    {label != null && label !== '' && (
                        <span className="ds-action-chip__label">{label}</span>
                    )}
                    {description != null && description !== '' && (
                        <span className="ds-action-chip__description">{description}</span>
                    )}
                </span>
            </button>
            {removable && (
                <button
                    type="button"
                    className={classNames('ds-action-chip__remove', slotClassNames.remove)}
                    aria-label={removeLabel}
                    onClick={onRemove}
                >
                    <Icon value={ACTION_CHIP_REMOVE_ICON} noMargin />
                </button>
            )}
        </span>
    );

    if (!tooltip) return chip;
    return <Tooltip title={tooltip}>{chip}</Tooltip>;
};

ActionChip.propTypes = {
    /** Icon name (without the `icon-svg-` prefix) shown before the text */
    icon: PropTypes.string,
    /** What the chip is — rendered in medium weight, followed by a colon in the design */
    label: PropTypes.node,
    /** The configured value, after the label */
    description: PropTypes.node,
    /** Opens the chip; omit it and the chip renders as static text */
    onClick: PropTypes.func,
    /** Removes the chip; omit it and no remove half renders */
    onRemove: PropTypes.func,
    /** Translated accessible name of the remove button */
    removeLabel: PropTypes.string,
    /** Hover tooltip — normally the full, untruncated text */
    tooltip: PropTypes.string,
    /** `muted` is the quieter surface, for a chip the reader cannot change */
    tone: PropTypes.oneOf(ACTION_CHIP_TONES),
    /** Greys the chip and disables both halves */
    disabled: PropTypes.bool,
    className: PropTypes.string,
    /** Extra classes on the inner parts: `{ main, remove }` — for call sites whose existing
     * selectors name those elements */
    slotClassNames: PropTypes.shape({
        main: PropTypes.string,
        remove: PropTypes.string,
    }),
};

export { ACTION_CHIP_TONE, ACTION_CHIP_TONES };
export default ActionChip;
